import React, { Component } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Chip,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import {
  Send as SubmitIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Undo as ReturnToDraftIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@material-ui/icons";
import { withStyles } from "@material-ui/core/styles";

const styles = (theme) => ({
  statusChip: {
    marginLeft: theme.spacing(1),
  },
  actionButton: {
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  approveButton: {
    backgroundColor: theme.palette.success.main,
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.success.dark,
    },
  },
  rejectButton: {
    backgroundColor: theme.palette.error.main,
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.error.dark,
    },
  },
  submitButton: {
    backgroundColor: theme.palette.info.main,
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.info.dark,
    },
  },
  returnButton: {
    backgroundColor: theme.palette.warning.main,
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.warning.dark,
    },
  },
});

class EnrollmentStatusManager extends Component {
  constructor(props) {
    super(props);
    this.state = {
      dialogOpen: false,
      dialogType: null,
      reason: "",
      loading: false,
    };
  }

  getStatusColor = (status) => {
    switch (status) {
      case "DRAFT":
        return "default";
      case "PENDING":
        return "primary";
      case "IN_REVIEW":
        return "secondary";
      case "APPROVED":
        return "success";
      case "REJECTED":
        return "error";
      case "EXPIRED":
        return "warning";
      case "CANCELLED":
        return "default";
      default:
        return "default";
    }
  };

  canEdit = () => {
    const { enrollment, userPermissions } = this.props;
    return (
      enrollment.status === "DRAFT" &&
      (enrollment.canEdit || userPermissions?.canEdit)
    );
  };

  canSubmit = () => {
    const { enrollment, userPermissions } = this.props;
    return (
      enrollment.status === "DRAFT" &&
      (enrollment.canEdit || userPermissions?.canSubmit)
    );
  };

  canReview = () => {
    const { enrollment, userPermissions } = this.props;
    return enrollment.status === "PENDING" && userPermissions?.canReview;
  };

  canApprove = () => {
    const { enrollment, userPermissions } = this.props;
    return enrollment.status === "IN_REVIEW" && userPermissions?.canApprove;
  };

  canReject = () => {
    const { enrollment, userPermissions } = this.props;
    return enrollment.status === "IN_REVIEW" && userPermissions?.canReject;
  };

  canReturnToDraft = () => {
    const { enrollment, userPermissions } = this.props;
    return (
      ["PENDING", "IN_REVIEW", "REJECTED"].includes(enrollment.status) &&
      userPermissions?.canReturnToDraft
    );
  };

  canDelete = () => {
    const { enrollment, userPermissions } = this.props;
    return enrollment.status === "DRAFT" && userPermissions?.canDelete;
  };

  handleOpenDialog = (type) => {
    this.setState({
      dialogOpen: true,
      dialogType: type,
      reason: "",
    });
  };

  handleCloseDialog = () => {
    this.setState({
      dialogOpen: false,
      dialogType: null,
      reason: "",
    });
  };

  handleReasonChange = (event) => {
    this.setState({ reason: event.target.value });
  };

  handleConfirmAction = async () => {
    const { dialogType, reason } = this.state;
    const { enrollment, onStatusChange } = this.props;

    this.setState({ loading: true });

    try {
      switch (dialogType) {
        case "submit":
          await onStatusChange("submit", enrollment.uuid, reason);
          break;
        case "review":
          await onStatusChange("review", enrollment.uuid, reason);
          break;
        case "approve":
          await onStatusChange("approve", enrollment.uuid, reason);
          break;
        case "reject":
          await onStatusChange("reject", enrollment.uuid, reason);
          break;
        case "returnToDraft":
          await onStatusChange("returnToDraft", enrollment.uuid, reason);
          break;
        case "delete":
          await onStatusChange("delete", enrollment.uuid, reason);
          break;
        default:
          break;
      }
      this.handleCloseDialog();
    } catch (error) {
      console.error("Status change error:", error);
    } finally {
      this.setState({ loading: false });
    }
  };

  renderDialogContent = () => {
    const { dialogType, reason } = this.state;

    const dialogConfig = {
      submit: {
        title: "Submit for Review",
        message: "Are you sure you want to submit this enrollment for review?",
        needsReason: false,
        reasonLabel: "Note",
      },
      review: {
        title: "Start Review",
        message: "Are you sure you want to start reviewing this enrollment?",
        needsReason: false,
        reasonLabel: "Review Note",
      },
      approve: {
        title: "Approve Enrollment",
        message: "Are you sure you want to approve this enrollment?",
        needsReason: false,
        reasonLabel: "Approval Reason",
      },
      reject: {
        title: "Reject Enrollment",
        message: "Are you sure you want to reject this enrollment?",
        needsReason: true,
        reasonLabel: "Rejection Reason",
      },
      returnToDraft: {
        title: "Return to Draft",
        message:
          "Are you sure you want to return this enrollment to draft status?",
        needsReason: true,
        reasonLabel: "Reason for returning to draft",
      },
      delete: {
        title: "Delete Enrollment",
        message:
          "Are you sure you want to delete this enrollment? This action cannot be undone.",
        needsReason: false,
        reasonLabel: "Deletion Reason",
      },
    };

    const config = dialogConfig[dialogType];
    if (!config) return null;

    return (
      <>
        <DialogTitle>{config.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{config.message}</DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label={config.reasonLabel}
            fullWidth
            multiline
            rows={3}
            value={reason}
            onChange={this.handleReasonChange}
            required={config.needsReason}
          />
        </DialogContent>
      </>
    );
  };

  render() {
    const { classes, enrollment } = this.props;
    const { dialogOpen, dialogType, reason, loading } = this.state;

    const needsReason = ["reject", "returnToDraft"].includes(dialogType);
    const isConfirmDisabled = loading || (needsReason && !reason.trim());

    return (
      <Box>
        {/* Status Display */}
        <Chip
          label={enrollment.status}
          color={this.getStatusColor(enrollment.status)}
          className={classes.statusChip}
        />

        {/* Action Buttons */}
        <Box mt={2}>
          {this.canEdit() && (
            <Tooltip title="Edit enrollment">
              <IconButton
                className={classes.actionButton}
                onClick={() => this.props.onEdit && this.props.onEdit()}
                color="primary"
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
          )}

          {this.canSubmit() && (
            <Button
              variant="contained"
              className={`${classes.actionButton} ${classes.submitButton}`}
              startIcon={<SubmitIcon />}
              onClick={() => this.handleOpenDialog("submit")}
            >
              Submit for Review
            </Button>
          )}

          {this.canReview() && (
            <Button
              variant="contained"
              className={classes.actionButton}
              onClick={() => this.handleOpenDialog("review")}
            >
              Start Review
            </Button>
          )}

          {this.canApprove() && (
            <Button
              variant="contained"
              className={`${classes.actionButton} ${classes.approveButton}`}
              startIcon={<ApproveIcon />}
              onClick={() => this.handleOpenDialog("approve")}
            >
              Approve
            </Button>
          )}

          {this.canReject() && (
            <Button
              variant="contained"
              className={`${classes.actionButton} ${classes.rejectButton}`}
              startIcon={<RejectIcon />}
              onClick={() => this.handleOpenDialog("reject")}
            >
              Reject
            </Button>
          )}

          {this.canReturnToDraft() && (
            <Button
              variant="contained"
              className={`${classes.actionButton} ${classes.returnButton}`}
              startIcon={<ReturnToDraftIcon />}
              onClick={() => this.handleOpenDialog("returnToDraft")}
            >
              Return to Draft
            </Button>
          )}

          {this.canDelete() && (
            <Tooltip title="Delete enrollment">
              <IconButton
                className={classes.actionButton}
                onClick={() => this.handleOpenDialog("delete")}
                color="secondary"
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Confirmation Dialog */}
        <Dialog open={dialogOpen} onClose={this.handleCloseDialog}>
          {this.renderDialogContent()}
          <DialogActions>
            <Button onClick={this.handleCloseDialog} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={this.handleConfirmAction}
              disabled={isConfirmDisabled}
              color="primary"
              variant="contained"
            >
              {loading ? "Processing..." : "Confirm"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }
}

export default withStyles(styles)(EnrollmentStatusManager);
